import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Search, PhoneCall, Download, Upload, Loader2, CheckCircle, AlertCircle, Trash2, Edit } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CallsList = () => {
    const { user } = useAuth();
    const [calls, setCalls] = useState([]);
    const [parents, setParents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingCall, setEditingCall] = useState(null);
    const [editForm, setEditForm] = useState({ notes: '', mobile_called: '', caller_name: '' });
    const [saving, setSaving] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        try {
            const [callsRes, parentsRes] = await Promise.all([
                axios.get(`${API_URL}/api/calls`, { withCredentials: true }),
                axios.get(`${API_URL}/api/parents`, { withCredentials: true })
            ]);
            setCalls(callsRes.data);
            setParents(parentsRes.data);
        } catch { toast.error('Failed to load calls'); }
        finally { setLoading(false); }
    };

    const getParentInfo = (id) => { const p = parents.find(p => p.id === id); return p ? { name: `${p.first_name} ${p.last_name}`, code: p.reg_code } : { name: 'Unknown', code: '—' }; };

    const handleExport = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/calls/export/excel`, { withCredentials: true, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a'); a.href = url; a.setAttribute('download', 'call_log.xlsx');
            document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
            toast.success('Call log exported');
        } catch { toast.error('Export failed'); }
    };

    const handleUpload = async (e) => {
        const file = e.target.files[0]; if (!file) return;
        setUploading(true); setUploadResult(null);
        const fd = new FormData(); fd.append('file', file);
        try {
            const res = await axios.post(`${API_URL}/api/calls/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
            setUploadResult(res.data);
            if (res.data.created > 0) { toast.success(`${res.data.created} call(s) imported`); fetchData(); }
        } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
        finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
    };

    const handleEdit = (call) => {
        setEditingCall(call);
        setEditForm({ notes: call.notes || '', mobile_called: call.mobile_called || '', caller_name: call.caller_name || '' });
        setEditDialogOpen(true);
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault(); setSaving(true);
        try {
            await axios.put(`${API_URL}/api/calls/${editingCall.id}`, editForm, { withCredentials: true });
            toast.success('Call updated'); setEditDialogOpen(false); fetchData();
        } catch { toast.error('Failed to update'); }
        finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this call record?')) return;
        try { await axios.delete(`${API_URL}/api/calls/${id}`, { withCredentials: true }); toast.success('Call deleted'); fetchData(); }
        catch { toast.error('Failed to delete'); }
    };

    const handleDeleteAll = async () => {
        if (!window.confirm('DELETE ALL call records? This cannot be undone!')) return;
        try { await axios.delete(`${API_URL}/api/calls`, { withCredentials: true }); toast.success('All calls deleted'); fetchData(); }
        catch { toast.error('Failed'); }
    };

    const filtered = calls.filter(c => {
        const pInfo = getParentInfo(c.parent_id);
        return c.caller_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
               c.notes?.toLowerCase().includes(searchQuery.toLowerCase()) ||
               pInfo.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
               pInfo.code?.includes(searchQuery);
    });

    if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div></div></DashboardLayout>;

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="calls-list-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Call Log</h1>
                        <p className="text-[#4B5563]">{calls.length} total calls</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button variant="outline" onClick={handleExport} data-testid="export-calls-btn"><Download className="h-4 w-4 mr-2" />Export Excel</Button>
                        <div>
                            <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleUpload} className="hidden" id="calls-upload" />
                            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-calls-btn">
                                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}Upload Excel
                            </Button>
                        </div>
                        <Button variant="outline" className="text-[#EF4444] border-[#EF4444]" onClick={handleDeleteAll} data-testid="delete-all-calls-btn" style={{display: user?.role === 'super_admin' ? undefined : 'none'}}><Trash2 className="h-4 w-4 mr-2" />Delete All</Button>
                    </div>
                </div>

                {uploadResult && (<div className="flex items-center gap-4 text-sm"><div className="flex items-center gap-1"><CheckCircle className="h-4 w-4 text-[#10B981]" />{uploadResult.created} imported</div>{uploadResult.skipped > 0 && <div className="flex items-center gap-1 text-[#F59E0B]"><AlertCircle className="h-4 w-4" />{uploadResult.skipped} skipped</div>}</div>)}

                <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-lg p-3 text-sm text-[#166534]">
                    <p className="font-medium">Excel format: <span className="font-mono">guardian_code, mobile_called, caller_name, time, notes</span></p>
                </div>

                <Card className="card-surface"><CardContent className="p-4"><div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" /><Input placeholder="Search calls..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" /></div></CardContent></Card>

                <Card className="card-surface"><CardContent className="p-0">
                    {filtered.length > 0 ? (
                        <div className="divide-y divide-[#E5E7EB]">
                            {filtered.map(c => { const pInfo = getParentInfo(c.parent_id); return (
                                <div key={c.id} className="p-4 hover:bg-[#F9FAFB]">
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex items-center gap-3">
                                            <div className="h-10 w-10 rounded-full bg-[#064E3B]/10 flex items-center justify-center"><PhoneCall className="h-5 w-5 text-[#064E3B]" /></div>
                                            <div>
                                                <p className="font-medium">{pInfo.name}</p>
                                                <div className="flex items-center gap-2 text-xs text-[#4B5563]"><Badge variant="outline" className="font-mono text-xs">{pInfo.code}</Badge><span>{c.mobile_called}</span></div>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-2">
                                            <div className="text-right">
                                                <p className="text-xs text-[#4B5563] tabular-nums">{new Date(c.created_at).toLocaleString()}</p>
                                                <p className="text-xs font-medium text-[#064E3B]">{c.caller_name}</p>
                                            </div>
                                            <div className="flex gap-1">
                                                <Button size="sm" variant="ghost" onClick={() => handleEdit(c)}><Edit className="h-3.5 w-3.5" /></Button>
                                                <Button size="sm" variant="ghost" className="text-[#EF4444]" onClick={() => handleDelete(c.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                                            </div>
                                        </div>
                                    </div>
                                    <p className="text-sm bg-[#FAF9F6] rounded-lg p-3 ml-[52px]">{c.notes || 'No notes'}</p>
                                </div>
                            ); })}
                        </div>
                    ) : <div className="p-12 text-center text-[#4B5563]">No calls recorded</div>}
                </CardContent></Card>

                {/* Edit Call Dialog */}
                <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                    <DialogContent>
                        <DialogHeader><DialogTitle className="font-['Outfit']">Edit Call Record</DialogTitle></DialogHeader>
                        <form onSubmit={handleEditSubmit} className="space-y-4">
                            <div className="space-y-2"><Label>Caller Name</Label><Input value={editForm.caller_name} onChange={e => setEditForm({...editForm, caller_name: e.target.value})} /></div>
                            <div className="space-y-2"><Label>Mobile Called</Label><Input value={editForm.mobile_called} onChange={e => setEditForm({...editForm, mobile_called: e.target.value})} /></div>
                            <div className="space-y-2"><Label>Notes</Label><textarea className="w-full min-h-[100px] rounded-md border border-[#D1D5DB] p-3 text-sm focus:ring-2 focus:ring-[#064E3B] focus:border-transparent" value={editForm.notes} onChange={e => setEditForm({...editForm, notes: e.target.value})} /></div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                                <Button type="submit" disabled={saving} className="bg-[#064E3B] hover:bg-[#047857]">{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save</Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
};

export default CallsList;
